// © Microsoft Corporation. All rights reserved.

import { DesignSystem, accentFillHover, accentFillRest, isDarkMode, neutralForegroundActive, neutralForegroundRest, neutralLayerCard, neutralLayerCardContainer } from "@microsoft/fast-components-styles-msft";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";
import { enums } from "../utils/enums";
import { profileHrColor } from "../utils/commonJssConstants";

const bgColor = "#F4F4F4";
const sectionWidth = "960px";
const borderColor = "#DDDDDD";
const tabIconColor = "#555555";
const tabLabelColor = "#333333";
const mobileSidePadding = "32px";

export const CommunityGuidelineStyles = (config: DesignSystem) => ({
    communityGuidelinePageContainer: {
        "padding-bottom": "48px",
        background: neutralLayerCardContainer,
        "border-bottom": "1px solid "+borderColor,

        "& .cwt-link": {
            color: accentFillRest,

            "&.cwt-big-link": {
                "font-weight": "bold",
                "text-transform": "uppercase"
            }
        },

        "& ul": {
            "list-style": "none",
            padding: "0"
        },

        "& .cwt-tab-content": {
            position: "relative",
            "min-height": "60vh",
            "margin-top": "48px",
            padding: "0 10px",
            color: neutralForegroundActive
        },

        "& .cwt-profile-tabs": {
            position: "sticky",
            top: "0",
            "z-index": "1",
            margin: "0",
            padding: "0 18px",
            "overflow-x": "scroll",
            "list-style": "none",

            // 'Hide scrollbar'
            "-ms-overflow-style": "none",

            "&::-webkit-scrollbar": {
                display: "none"
            },

            "& .cwt-tab-item": {
                position: "relative",
                padding: "15px 0",
                color: tabIconColor,
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "24px",

                "&:first-child": {
                    [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "0"
                },

                "&.active": {
                    color: accentFillRest,
                    "font-weight": "600",

                    "&:after": {
                        position: "absolute",
                        "z-index": "10",
                        content: "' '",
                        height: "3px",
                        width: "100%",
                        "margin-top": "calc(15px - 3px)",
                        [applyLocalizedProperty("left", "right", config.direction)]: "0",
                        background: accentFillRest
                    },

                    "& .cwt-icon-label": {
                        color: isDarkMode(config) ? "#BDDCFF" : accentFillRest
                    }
                },

                "& .cwt-icon": {
                    color: "inherit",
                    "white-space": "nowrap",
                    height: "18px"
                },

                "& .cwt-icon-label": {
                    color: neutralForegroundActive,
                    "font-size": "13px"
                }
            }
        },

        "& .cwt-section": {
            "max-width": sectionWidth,
            margin: "0 auto",
            padding: "0",
            "font-size": "13px"
        },

        "& .cwt-section-list": {
            "margin-bottom": "32px"
        },

        "& .cwt-main-header": {
            position: "relative",
            background: neutralLayerCard,
            color: neutralForegroundActive,
            "letter-spacing": applyLocalizedProperty(".5pt", "0", config.direction),
            padding: "0 10px",

            "& > .cwt-section": {
                position: "relative",
                padding: "32px 0 24px",

                "& .cwt-title": {
                    "margin-top": "24px",
                    "font-size": "38px",
                    "font-weight": "600"
                },
                "& .cwt-image-title": {
                    display: "flex",
                    "align-items": "center",

                    "& > .cwt-image": {
                        height: "22px"
                    },

                    "& > .cwt-sub-title": {
                        margin: "0 18px",
                        "font-size": "11px",
                        "font-weight": "bold",
                        "text-transform": "uppercase"
                    }
                }
            },

            "& .cwt-tabs-container": {
                "border-top": "1px solid ",
                "border-color": profileHrColor
            }
        },

        "& .cwt-guideline-section": {
            display: "flex",
            "align-items": "center",
            margin: "28px 0 0",
            "padding-bottom": "42px",
            color: neutralForegroundRest,
            "border-bottom": "1px solid "+borderColor,

            "& > .cwt-image": {
                width: "200px"
            },

            "& > .cwt-content": {
                margin: "0 48px",
                "font-size": "16px",

                "& > .cwt-subject-list > li:before": {
                    content: "'•'",
                    margin: "3px"
                }
            }
        },

        "& .cwt-guideline-detail-container": {
            display: "flex",
            "justify-content": "space-between",
            "font-size": "16px",

            "& > .cwt-image": {
                "max-width": "50%",
                margin: "0 50px",
                "object-fit": "contain"
            },

            "& .cwt-principles-detail": {
                "max-width": "560px",
                "& a": {
                    color: isDarkMode(config) ? "#BDDCFF" : accentFillHover

                }
            },

            "& .cwt-enforcement-detail": {
                "max-width": "640px"
            }
        },

        "& .cwt-inner-nav": {
            "margin-top": "30px",

            "& .cwt-buttons": {
                display: "flex",
                padding: "7px 0",
                "flex-wrap": "wrap",

                "& .cwt-fast-button": {
                    "min-width": "130px",
                    "margin-bottom": "5px",
                    [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "9px"
                }
            }
        },

        /** RESPONSIVE STYLES */
        ["&." + enums.responsiveLevels.L0]: {
            "& .cwt-title-section": {
                margin: "0 "+mobileSidePadding
            },
            "& .cwt-tabs-container": {
                padding: "0 "+mobileSidePadding
            },

            "& .cwt-guideline-section": {
                "flex-direction": "column",

                "& > .cwt-content": {
                    padding: "0 " + mobileSidePadding,
                    width: `calc(100% - ${mobileSidePadding} * 2)`
                }
            },

            "& .cwt-guideline-detail-container": {
                "flex-direction": "column-reverse",
                "align-items": "center",

                "& > .cwt-content": {
                    margin: "0 "+mobileSidePadding
                },

                "& > .cwt-image": {
                    "max-width": "90%",
                    width: "310px"
                }
            },
            "& .cwt-tab-content": {
                "& .cwt-inner-nav": {
                    "& .cwt-buttons": {
                        margin: "0 " + mobileSidePadding
                    }
                }
            }
        }
    }
});